import { College, ResourcePages, addHttpIfMissing } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { handleBadgeClick } from '@backrs_ui/grow-ui/src/components/ResourceWrapper/resource.lubav.util';
import { ResourceHeader } from '@backrs_ui/grow-ui/src/components/ResourceWrapper/ResourceHeader';
import { ResourceWrapper } from '@backrs_ui/grow-ui/src/components/ResourceWrapper/ResourceWrapper';
import { TheScoop } from '@backrs_ui/grow-ui/src/components/ResourceWrapper/TheScoop';
import {
  ResourcePageSection,
  ScoopStatItem,
} from '@backrs_ui/grow-ui/src/components/ResourceWrapper/types';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { selectMakeItPersonalBadgesByType } from '@backrs_ui/grow-ui/src/redux/makeItPersonal/makeItPersonal';

import { determineCollegeSize, determineDegreeType } from './college.util';
import { ReturnOnInvestment } from './molecules/ReturnOnInvestment';
import { TheCost } from './molecules/TheCost';
import { WhoGetsIn } from './molecules/WhoGetsIn';

const SCOOP_STRINGS = appStrings.modules.college.scoop;
const COLLEGE_HEADER_STRINGS = appStrings.modules.college.header;

type CollegePageProps = Readonly<{
  college: College;
  handleBookmark: () => void;
  hasBookmarked: boolean;
}>;

export const CollegePage = ({ college, handleBookmark, hasBookmarked }: CollegePageProps) => {
  const dispatch = useAppDispatch();

  const userId = useAppSelector((state) => state.user.user?.id);
  const { activeMakeItPersonalBadge } = useAppSelector((state) => state.makeItPersonal);

  // filter the badges to only include the college badges
  const badges = useAppSelector(selectMakeItPersonalBadgesByType(ResourcePages.COLLEGE));

  const summaryItems = [
    {
      title: SCOOP_STRINGS.bestKnownFor,
      description: college.scoop_best_known_for || '',
    },
    {
      title: SCOOP_STRINGS.vibeCheck,
      description: college.scoop_vibe_check || '',
    },
    {
      title: SCOOP_STRINGS.opportunitiesNetworks,
      description: college.scoop_opportunities_networks || '',
    },
  ];

  const statItems: ScoopStatItem[] = [
    {
      title: SCOOP_STRINGS.enrollment,
      iconName: 'GraduationHat02',
      text: determineDegreeType(college.predominant_degree),
    },
    {
      title: SCOOP_STRINGS.type,
      iconName: 'Flag02',
      text: college.type || '',
    },
    {
      title: SCOOP_STRINGS.size,
      iconName: 'Building08',
      text: determineCollegeSize(college.undergrad_enrollment),
    },
    {
      title: SCOOP_STRINGS.setting,
      iconName: 'Building07',
      text: college.setting || '',
    },
  ];

  const PAGE_SECTIONS: ResourcePageSection[] = [
    {
      id: 'the-scoop',
      title: 'The Scoop',
      component: <TheScoop summaryItems={summaryItems} statItems={statItems} />,
    },
    {
      id: 'who-gets-in',
      title: 'Who Gets In',
      component: <WhoGetsIn college={college} />,
    },
    {
      id: 'the-cost',
      title: 'The Cost',
      component: <TheCost college={college} />,
    },
    {
      id: 'roi',
      title: 'ROI',
      component: <ReturnOnInvestment college={college} />,
    },
  ];

  return (
    <ResourceWrapper
      header={
        <ResourceHeader
          page={ResourcePages.COLLEGE}
          title={college.school_name}
          banner_media_url={college.banner_media_url || undefined}
          logo_media_url={college.logo_media_url || '/images/university-logo-default.svg'}
          city={college.city}
          state={college.state}
          buttonMain={{
            label: hasBookmarked
              ? COLLEGE_HEADER_STRINGS.buttons.bookmarkBookmarked
              : COLLEGE_HEADER_STRINGS.buttons.bookmarkNotBookmarked,
            onClick: handleBookmark,
            icon: hasBookmarked ? 'BookmarkCheck' : 'Bookmark',
          }}
          buttonSecondary={{
            label: COLLEGE_HEADER_STRINGS.buttons.website,
            onClick: () => {
              if (college.url) {
                window.open(addHttpIfMissing(college.url), '_blank');
              }
            },
            icon: 'LinkExternal01',
          }}
        />
      }
      sections={PAGE_SECTIONS}
      chipLabel={college.school_name}
      makeItPersonalProps={{
        pageName: ResourcePages.COLLEGE,
        badges,
        activeBadge: activeMakeItPersonalBadge,
        onBadgeClick: (badge) =>
          handleBadgeClick({
            page: ResourcePages.COLLEGE,
            badge,
            activeMakeItPersonalBadge,
            college,
            userId,
            dispatch,
          }),
      }}
    />
  );
};
